/**
 * @module features/upload/upload-form
 * @description Handles upload form interactions, file selection, validation, and rendering
 */

import { getFileIcon } from '../files/file-utils.js';
import { formatFileSize } from '../../utils/formatters.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { populateCategorySelect, getDefaultCategoryId } from './file-categories.js';
import { validateFileAgainstConstraints, getMaxFilesPerRequest } from './upload-constraints.js';

// Module state
let selectedFiles = [];
let onFilesChangedCallback = null;

/**
 * Set callback for when files change
 * @param {Function} callback - Callback function to call when files change
 */
export function setOnFilesChanged(callback) {
    onFilesChangedCallback = callback;
}

/**
 * Get currently selected files
 * @returns {Array} Array of file items
 */
export function getSelectedFiles() {
    return selectedFiles;
}

/**
 * Clear all selected files
 */
export function clearSelectedFiles() {
    selectedFiles = [];
    notifyFilesChanged();
}

/**
 * Handle drag over event
 * @param {DragEvent} e - Drag event
 * @param {HTMLElement} dropZone - Drop zone element
 */
export function handleDragOver(e, dropZone) {
    e.preventDefault();
    dropZone.classList.add('drag-over');
}

/**
 * Handle drag leave event
 * @param {DragEvent} e - Drag event
 * @param {HTMLElement} dropZone - Drop zone element
 */
export function handleDragLeave(e, dropZone) {
    e.preventDefault();
    dropZone.classList.remove('drag-over');
}

/**
 * Handle file drop event
 * @param {DragEvent} e - Drop event
 * @param {HTMLElement} dropZone - Drop zone element
 */
export function handleFileDrop(e, dropZone) {
    e.preventDefault();
    dropZone.classList.remove('drag-over');

    const files = Array.from(e.dataTransfer.files);
    addFilesToUpload(files);
}

/**
 * Handle file selection from input
 * @param {Event} e - Change event from file input
 */
export function handleFileSelection(e) {
    const files = Array.from(e.target.files);
    addFilesToUpload(files);
    // Clear the input so the same file can be selected again
    e.target.value = '';
}

/**
 * Add files to the upload list
 * @param {Array<File>} files - Array of File objects
 */
export function addFilesToUpload(files) {
    const rejections = [];
    const validFiles = [];

    files.forEach(file => {
        const { valid, reason } = validateFileAgainstConstraints(file);
        if (valid) {
            validFiles.push(file);
        } else {
            rejections.push(reason);
        }
    });

    // Enforce the server's batch limit here too, so the user is told before uploading
    // rather than having the whole request rejected
    const maxFiles = getMaxFilesPerRequest();
    let acceptedFiles = validFiles;

    if (maxFiles) {
        const remainingSlots = Math.max(0, maxFiles - selectedFiles.length);
        if (validFiles.length > remainingSlots) {
            acceptedFiles = validFiles.slice(0, remainingSlots);
            rejections.push(`Only ${maxFiles} files can be uploaded at once. ${validFiles.length - remainingSlots} file(s) were not added.`);
        }
    }

    acceptedFiles.forEach(file => {
        const fileId = generateFileId();
        const defaultCategoryId = getDefaultCategoryId();

        selectedFiles.push({
            id: fileId,
            file: file,
            categoryId: defaultCategoryId,
            status: 'pending',
            progress: 0
        });
    });

    // Report every rejection at once - reporting them one at a time meant only the
    // last was ever shown
    if (rejections.length > 0 && onFilesChangedCallback) {
        onFilesChangedCallback(selectedFiles, { error: rejections.join(' ') });
        return;
    }

    notifyFilesChanged();
}

/**
 * Generate unique file ID
 * @returns {string} Unique file identifier
 */
function generateFileId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * Render the upload files list
 * @param {HTMLElement} container - Container element for files list
 * @param {HTMLElement} listWrapper - Wrapper element that can be shown/hidden
 */
export function renderUploadFilesList(container, listWrapper) {
    if (selectedFiles.length === 0) {
        listWrapper.classList.add('hidden');
        return;
    }

    listWrapper.classList.remove('hidden');
    container.innerHTML = '';

    selectedFiles.forEach((fileItem) => {
        const fileElement = createUploadFileElement(fileItem);
        container.appendChild(fileElement);
    });
}

/**
 * Create upload file element
 * @param {Object} fileItem - File item object
 * @returns {HTMLElement} File element
 */
function createUploadFileElement(fileItem) {
    const div = document.createElement('div');
    div.className = 'upload-file-item';
    div.dataset.fileId = fileItem.id;

    const icon = getFileIcon(fileItem.file.name);
    const size = formatFileSize(fileItem.file.size);

    // Status text can carry a server-supplied message containing the filename, so it
    // must be escaped before being interpolated into innerHTML
    const statusText = escapeHtml(getStatusText(fileItem));

    div.innerHTML = `
        <div class="upload-file-icon">${icon}</div>
        <div class="upload-file-info">
            <div class="upload-file-name">${escapeHtml(fileItem.file.name)}</div>
            <div class="upload-file-size">${size}</div>
            ${fileItem.status !== 'pending' ? `
                <div class="upload-file-progress">
                    <div class="upload-progress-bar">
                        <div class="upload-progress-fill" style="width: ${fileItem.progress}%"></div>
                    </div>
                    <div class="upload-file-status ${fileItem.status}" title="${statusText}">${statusText}</div>
                </div>
            ` : ''}
        </div>
        <div class="upload-file-category">
            <select ${fileItem.status !== 'pending' ? 'disabled' : ''}>
                <option value="">Select category...</option>
            </select>
        </div>
        <div class="upload-file-actions">
            ${fileItem.status === 'pending' ? `<button class="upload-file-remove" data-file-id="${fileItem.id}">Remove</button>` : ''}
        </div>
    `;

    // Add event listeners
    const categorySelect = div.querySelector('select');
    const removeBtn = div.querySelector('.upload-file-remove');

    if (categorySelect) {
        // Populate category options
        populateCategorySelect(categorySelect, fileItem.categoryId);

        categorySelect.addEventListener('change', (e) => {
            const fileItem = selectedFiles.find(f => f.id === div.dataset.fileId);
            if (fileItem) {
                fileItem.categoryId = e.target.value;
                notifyFilesChanged();
            }
        });
    }

    if (removeBtn) {
        removeBtn.addEventListener('click', () => {
            selectedFiles = selectedFiles.filter(f => f.id !== fileItem.id);
            notifyFilesChanged();
        });
    }

    return div;
}

/**
 * Get status text for a file item
 * @param {Object} fileItem - File item object
 * @returns {string} Status text
 */
function getStatusText(fileItem) {
    switch (fileItem.status) {
        case 'uploading':
            return `Uploading... ${Math.round(fileItem.progress)}%`;
        case 'success':
            return 'Upload successful';
        case 'error':
            return fileItem.errorMessage || 'Upload failed';
        default:
            return '';
    }
}

/**
 * Update file item status
 * @param {string} fileId - File ID
 * @param {string} status - New status (pending, uploading, success, error)
 * @param {number} progress - Progress percentage (0-100)
 * @param {string} errorMessage - Error message if status is error
 */
export function updateFileStatus(fileId, status, progress = 0, errorMessage = null) {
    const fileItem = selectedFiles.find(f => f.id === fileId);
    if (fileItem) {
        fileItem.status = status;
        fileItem.progress = progress;
        if (errorMessage) {
            fileItem.errorMessage = errorMessage;
        }
    }
}

/**
 * Check if upload button should be enabled
 * @returns {boolean} True if upload should be enabled
 */
export function shouldEnableUpload() {
    const hasFiles = selectedFiles.length > 0;
    const allHaveCategories = selectedFiles.every(f => f.categoryId);
    const noneUploading = selectedFiles.every(f => f.status === 'pending');

    return hasFiles && allHaveCategories && noneUploading;
}

/**
 * Notify that files have changed
 */
function notifyFilesChanged() {
    if (onFilesChangedCallback) {
        onFilesChangedCallback(selectedFiles);
    }
}

/**
 * Mark all uploading files as failed
 * @param {string} errorMessage - Error message
 */
export function markUploadingAsFailed(errorMessage) {
    selectedFiles.forEach(fileItem => {
        if (fileItem.status === 'uploading') {
            fileItem.status = 'error';
            fileItem.errorMessage = errorMessage;
        }
    });
}

/**
 * Update file statuses from the server's per-file results.
 *
 * The API reports an entry per file (UploadEntityFilesResponseDto.files) carrying
 * success and, on failure, the reason. That reason is the only way the user can
 * learn why an upload was rejected, so it is surfaced on the file's row.
 *
 * @param {Array<Object>} serverFiles - Per-file results from the API
 * @param {number} successCount - Number of successful uploads, used only as a fallback
 */
export function applyUploadResults(serverFiles, successCount) {
    const uploadingFiles = selectedFiles.filter(f => f.status === 'uploading');

    if (!Array.isArray(serverFiles) || serverFiles.length === 0) {
        // No per-file detail available. Nothing identifies which files failed, so
        // fall back to assuming the first successCount succeeded.
        uploadingFiles.forEach((fileItem, index) => {
            if (index < successCount) {
                fileItem.status = 'success';
                fileItem.progress = 100;
            } else {
                fileItem.status = 'error';
                fileItem.errorMessage = 'Upload failed';
            }
        });
        return;
    }

    const unmatchedResults = [...serverFiles];

    uploadingFiles.forEach(fileItem => {
        // Match on filename so results stay correct regardless of the order the
        // server reports them in; fall back to the next unmatched result, which
        // handles duplicate filenames within one batch
        let resultIndex = unmatchedResults.findIndex(r => r?.originalFileName === fileItem.file.name);
        if (resultIndex === -1) {
            resultIndex = 0;
        }

        const fileResult = unmatchedResults.splice(resultIndex, 1)[0];

        if (!fileResult) {
            fileItem.status = 'error';
            fileItem.errorMessage = 'The server returned no result for this file';
            return;
        }

        if (fileResult.success) {
            fileItem.status = 'success';
            fileItem.progress = 100;
        } else {
            fileItem.status = 'error';
            fileItem.errorMessage = fileResult.errorMessage || 'Upload failed';
        }
    });
}

/**
 * Summarise why files failed, for the modal-level error message
 * @returns {string} A summary, or an empty string if nothing failed
 */
export function getUploadFailureSummary() {
    const reasons = selectedFiles
        .filter(f => f.status === 'error' && f.errorMessage)
        .map(f => f.errorMessage);

    const distinctReasons = [...new Set(reasons)];

    if (distinctReasons.length === 0) {
        return '';
    }

    if (distinctReasons.length === 1) {
        return distinctReasons[0];
    }

    return 'See each file for details.';
}
